import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { apiResources } from 'app/services/api/api.resources';

// ---------------------------------------------------------------------------
// Tipos do contrato — Ajuda do sistema (/v1/help/*)
// ---------------------------------------------------------------------------

/** Artigo citado como fonte da resposta do assistente. */
export interface HelpSourceArticle {
    id: number;
    title: string;
    slug: string;
}

/** Payload de sucesso do POST /v1/help/chat. */
export interface HelpChatData {
    conversationId: string;
    answer: string;
    sourceArticle: HelpSourceArticle | null;
    suggestedFollowups: HelpSourceArticle[];
}

export interface HelpChatResponse {
    success: boolean;
    data: HelpChatData;
}

/** Resumo de artigo (item da listagem /v1/help/articles). */
export interface HelpArticleSummary {
    id: number;
    title: string;
    slug: string;
    category: string;
    summary: string;
    sortOrder: number;
}

/** Artigo completo (GET /v1/help/articles/{slug}). */
export interface HelpArticle extends HelpArticleSummary {
    body: string;
}

export interface HelpArticlesMeta {
    current_page: number;
    last_page: number;
    total: number;
    per_page: number;
}

export interface HelpArticlesData {
    items: HelpArticleSummary[];
    meta: HelpArticlesMeta;
    categories: string[];
}

export interface HelpArticlesResponse {
    success: boolean;
    data: HelpArticlesData;
}

export interface HelpArticleResponse {
    success: boolean;
    data: HelpArticle;
}

/** Envelope de erro das APIs de help (422/402/429/503). */
export interface HelpErrorBody {
    message?: string;
    code?: number;
    errors?: Record<string, string[]>;
}

export interface HelpListArticlesParams {
    term?: string;
    category?: string;
    page?: number;
    perPage?: number;
}

@Injectable({ providedIn: 'root' })
export class HelpChatService {
    private readonly _baseUrl =
        environment.config.apiUrl +
        apiResources.v1 +
        apiResources.help.base;

    constructor(private _httpClient: HttpClient) {}

    /**
     * Envia uma pergunta ao assistente. O `conversationId` é opcional —
     * omite-o (ou passa null) para iniciar um novo diálogo; o backend devolve
     * o id da conversa na resposta.
     */
    ask(message: string, conversationId?: string | null): Observable<HelpChatResponse> {
        const body: { message: string; conversationId?: string } = { message };

        if (conversationId) {
            body.conversationId = conversationId;
        }

        return this._httpClient.post<HelpChatResponse>(
            this._baseUrl + apiResources.help.chat,
            body
        );
    }

    /**
     * Lista artigos com busca (term), filtro por categoria e paginação.
     * `perPage` é enviado em camelCase (padrão já aceito pelo backend).
     */
    listArticles(params: HelpListArticlesParams): Observable<HelpArticlesResponse> {
        let httpParams = new HttpParams();

        if (params.term && params.term.trim()) {
            httpParams = httpParams.set('term', params.term.trim());
        }

        if (params.category) {
            httpParams = httpParams.set('category', params.category);
        }

        if (params.page) {
            httpParams = httpParams.set('page', String(params.page));
        }

        if (params.perPage) {
            httpParams = httpParams.set('perPage', String(params.perPage));
        }

        return this._httpClient.get<HelpArticlesResponse>(
            this._baseUrl + apiResources.help.articles,
            { params: httpParams }
        );
    }

    /** Busca um artigo completo pelo slug. */
    getArticle(slug: string): Observable<HelpArticleResponse> {
        return this._httpClient.get<HelpArticleResponse>(
            this._baseUrl + apiResources.help.articles + '/' + encodeURIComponent(slug)
        );
    }
}